import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, CalendarClock, Users, Clock } from "lucide-react";

const STATUS_COLOR = {
  confirmed: "bg-blue-100 text-blue-800",
  seated: "bg-green-100 text-green-800",
  completed: "bg-neutral-100 text-neutral-600",
  cancelled: "bg-red-100 text-red-700",
};

export default function Reservations() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ customer_name: "", phone: "", guests: 2, date: today, time: "19:00", table_id: "", notes: "" });

  const { data: reservations = [] } = useQuery({ queryKey: ["reservations"], queryFn: async () => (await api.get("/reservations")).data });
  const { data: tables = [] } = useQuery({ queryKey: ["tables"], queryFn: async () => (await api.get("/tables")).data });

  const add = async () => {
    if (!form.customer_name || !form.phone) return toast.error("Name & phone required");
    await api.post("/reservations", { ...form, guests: Number(form.guests), table_id: form.table_id || null, status: "confirmed" });
    toast.success("Reservation booked"); setOpen(false);
    qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["tables"] });
  };
  const setStatus = async (r, status) => {
    await api.put(`/reservations/${r.id}`, { ...r, status });
    toast.success(`Marked ${status}`);
    qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["tables"] });
  };
  const tblName = (id) => tables.find((t) => t.id === id)?.name || "—";

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Reservations" subtitle="Manage table bookings">
        <Button onClick={() => setOpen(true)} data-testid="add-res-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> New Booking</Button>
      </PageHeader>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {reservations.map((r) => (
          <div key={r.id} className="kul-card p-4" data-testid={`res-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold">{r.customer_name}</div>
              <Badge className={`capitalize ${STATUS_COLOR[r.status]}`}>{r.status}</Badge>
            </div>
            <div className="text-sm text-neutral-500 mt-1">{r.phone}</div>
            <div className="flex items-center gap-4 mt-3 text-sm text-neutral-600">
              <span className="flex items-center gap-1"><CalendarClock className="w-4 h-4" /> {r.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {r.time}</span>
              <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {r.guests}</span>
            </div>
            <div className="text-xs text-neutral-400 mt-1">Table: {tblName(r.table_id)}</div>
            {!["cancelled", "completed"].includes(r.status) && (
              <div className="flex gap-1 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setStatus(r, "seated")} data-testid={`seat-${r.id}`}>Seat</Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setStatus(r, "completed")}>Complete</Button>
                <Button size="sm" variant="ghost" className="text-[var(--kul-danger)]" onClick={() => setStatus(r, "cancelled")}>Cancel</Button>
              </div>
            )}
          </div>
        ))}
        {!reservations.length && <p className="text-neutral-400 col-span-full text-center py-16">No reservations yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Reservation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="res-name" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="res-phone" /></div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="res-date" /></div>
              <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="res-time" /></div>
              <div><Label>Guests</Label><Input type="number" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="res-guests" /></div>
              <div><Label>Table</Label><select value={form.table_id} onChange={(e) => setForm({ ...form, table_id: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="res-table"><option value="">Any</option>{tables.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={add} className="bg-[var(--kul-primary)] text-white" data-testid="save-res">Book</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
